import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { ElementCard, fmtIso } from "@/components/SignOnLive";

const fmtDay = (d) => d.split("-").reverse().join("/");

export default function CommanderBoard() {
  const { token } = useParams();
  const [day, setDay] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get(`/public/commander/${token}`, { params: day ? { day } : {} })
      .then(({ data }) => { setData(data); setError(""); })
      .catch((e) => {
        setError(e.response?.data?.detail || "Could not load the board");
        if ([404, 410].includes(e.response?.status)) setData(null);
      });
  }, [token, day]);
  const dead = !!error && !data;
  useEffect(() => {
    load();
    if (dead) return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load, dead]);

  if (error && !data) return (
    <div className="flex min-h-screen items-center justify-center bg-[#F4F4F5] p-6 text-center" data-testid="commander-board-error">
      <div><AlertTriangle className="mx-auto h-8 w-8 text-[#FF3B30]" /><p className="mt-3 text-sm">{error}</p></div>
    </div>
  );
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-[#F4F4F5]"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const signed = data.elements.reduce((a, e) => a + e.signed, 0);
  const expected = data.elements.reduce((a, e) => a + e.expected, 0);
  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="commander-board">
      <header className="border-b border-black/10 bg-white">
        <div className="mx-auto max-w-3xl px-5 py-4">
          <div className="font-display text-xl font-black tracking-tighter">VOLMEE<span className="text-[#FF3B30]">.</span></div>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-[#002FA7]">{data.event.name}</div>
          <h1 className="font-display text-3xl font-black tracking-tighter" data-testid="commander-board-title">Live sign-on board</h1>
          <p className="text-xs text-zinc-500">For {data.commander.name} · view only</p>
        </div>
      </header>
      <main className="mx-auto max-w-3xl space-y-4 px-5 py-6">
        <div className="flex flex-wrap items-center gap-2">
          {data.days.length > 1 ? (
            <select value={data.day} onChange={(e) => setDay(e.target.value)} data-testid="commander-board-day-select" className="border border-black/20 bg-white px-3 py-2 text-sm">
              {data.days.map((d) => <option key={d} value={d}>{fmtDay(d)}{d === data.today ? " (today)" : ""}</option>)}
            </select>
          ) : <span className="text-sm font-semibold">{fmtDay(data.day)}</span>}
          <span className="font-mono text-sm" data-testid="commander-board-total">{signed}/{expected} signed on</span>
          <span className="text-xs text-zinc-500">Updates every 15s · last {fmtIso(data.generated_at)}</span>
          <button onClick={load} data-testid="commander-board-refresh-btn" className="ml-auto inline-flex items-center gap-1 border border-black/20 bg-white px-2 py-1.5 text-xs hover:bg-black hover:text-white"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
        </div>
        {error ? <div className="border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div> : null}
        {data.elements.map((el) => <ElementCard key={el.session} el={el} readOnly />)}
        {!data.elements.length ? <p className="text-sm text-zinc-500">No event elements for this day.</p> : null}
      </main>
    </div>
  );
}
